import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Lock, Unlock, ShoppingCart, User, LogOut, Shield, Mail, Home, Clock, Bell, BellRing } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import Footer from '../components/Footer';

const API = `${'https://shopblkmrkt.com'}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_91be65d6-9fee-4eda-abde-3fd83cab62ae/artifacts/kxr3j7eu_IMG_7793.png";

// Large Countdown Timer Component for scheduled drops
function DropCountdown({ scheduledTime, onComplete }) {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!scheduledTime) {
      setIsActive(false);
      return;
    }

    const calculateTime = () => {
      const now = new Date();
      const end = new Date(scheduledTime);
      const diff = end - now;

      if (diff <= 0) {
        setIsActive(false);
        if (onComplete) onComplete();
        return;
      }

      setIsActive(true);
      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTime();
    const interval = setInterval(calculateTime, 1000);
    return () => clearInterval(interval);
  }, [scheduledTime, onComplete]);

  if (!isActive) return null;

  const TimeBox = ({ value, label }) => (
    <div className="flex flex-col items-center">
      <div className="bg-zinc-900 border border-zinc-700 px-3 sm:px-6 py-3 sm:py-4 min-w-[60px] sm:min-w-[90px]">
        <span className="font-mono text-2xl sm:text-5xl text-tiffany-500 font-bold">
          {String(value).padStart(2, '0')}
        </span>
      </div>
      <span className="font-mono text-[10px] sm:text-xs text-zinc-500 mt-2 tracking-widest">{label}</span>
    </div>
  );

  return (
    <div className="text-center space-y-6 fade-in" data-testid="drop-countdown">
      <div className="flex items-center justify-center gap-2 text-zinc-400">
        <Clock className="w-4 h-4" />
        <span className="font-mono text-xs tracking-widest">NEXT DROP IN</span>
      </div>
      <div className="flex items-center justify-center gap-2 sm:gap-4">
        {timeLeft.days > 0 && <TimeBox value={timeLeft.days} label="DAYS" />}
        <TimeBox value={timeLeft.hours} label="HOURS" />
        <span className="text-zinc-600 text-2xl sm:text-4xl font-mono self-start mt-3 sm:mt-4">:</span>
        <TimeBox value={timeLeft.minutes} label="MIN" />
        <span className="text-zinc-600 text-2xl sm:text-4xl font-mono self-start mt-3 sm:mt-4">:</span>
        <TimeBox value={timeLeft.seconds} label="SEC" />
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const { user, logout, authResolved, hasToken, hasValidToken } = useAuth();
  const { itemCount } = useCart();
  const [dropStatus, setDropStatus] = useState({ is_open: false, access_code: null, drop_name: null, scheduled_drop_time: null });
  const [accessCode, setAccessCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  
  // State for drop reminders
  const [reminderEmail, setReminderEmail] = useState('');
  const [reminderSubscribed, setReminderSubscribed] = useState(false);
  const [showReminderInput, setShowReminderInput] = useState(false);
  const [subscribingReminder, setSubscribingReminder] = useState(false);

  useEffect(() => {
    fetchDropStatus();
    // Note: We don't check sessionStorage here - fetchDropStatus will handle access logic
    // This prevents passcode bypass when drop status changes
  }, []);

  const fetchDropStatus = async () => {
    try {
      const response = await axios.get(`${API}/drop/status`);
      setDropStatus(response.data);
      
      // Security: Always determine access based on current drop status
      if (response.data.is_open) {
        // Drop is open to everyone - no code needed
        setHasAccess(true);
        sessionStorage.setItem('blkmrkt_access', 'granted');
      } else if (response.data.access_code) {
        // Drop is closed but has an access code - check if user previously entered correct code
        const storedAccess = sessionStorage.getItem('blkmrkt_access');
        const storedCode = sessionStorage.getItem('blkmrkt_access_code');
        const hasTempAccess = sessionStorage.getItem('blkmrkt_temp_access') === 'true';
        
        // Grant access if: temp code was used OR stored code matches main access code
        if (storedAccess === 'granted' && (hasTempAccess || storedCode === response.data.access_code)) {
          setHasAccess(true);
        } else {
          // Access code changed or user never entered it - revoke access
          setHasAccess(false);
          sessionStorage.removeItem('blkmrkt_access');
          sessionStorage.removeItem('blkmrkt_access_code');
          sessionStorage.removeItem('blkmrkt_temp_access');
        }
      } else {
        // Drop is closed with no access code (fully locked)
        setHasAccess(false);
        sessionStorage.removeItem('blkmrkt_access');
        sessionStorage.removeItem('blkmrkt_access_code');
        sessionStorage.removeItem('blkmrkt_temp_access');
      }
    } catch (error) {
      console.error('Failed to fetch drop status:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleReminderSubscribe = async () => {
    if (!reminderEmail || !reminderEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      setSubscribingReminder(true);
      await axios.post(`${API}/drop/reminder/subscribe`, { email: reminderEmail });
      toast.success("You'll receive an email 10 minutes before the drop!");
      setReminderSubscribed(true);
      setShowReminderInput(false);
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to subscribe to reminder');
      }
    } finally {
      setSubscribingReminder(false);
    }
  };

  // Called when countdown reaches zero
  const handleCountdownComplete = () => {
    // Refresh drop status to get updated state
    fetchDropStatus();
  };

  const checkAccessCode = async () => {
    try {
      const response = await axios.post(`${API}/drop/check-access`, { code: accessCode });
      if (response.data.valid) {
        setHasAccess(true);
        sessionStorage.setItem('blkmrkt_access', 'granted');
        // If it's a temp code, store a special flag so it bypasses main code check
        if (response.data.temp_code) {
          sessionStorage.setItem('blkmrkt_temp_access', 'true');
        }
        // Store the code that was successfully validated
        sessionStorage.setItem('blkmrkt_access_code', accessCode.toUpperCase());
        toast.success('ACCESS GRANTED');
      } else {
        toast.error('INVALID CODE');
      }
    } catch (error) {
      toast.error('Failed to verify code');
    }
  };

  const handleSubscribe = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      setSubscribing(true);
      await axios.post(`${API}/mailing-list/subscribe`, { email });
      toast.success('Welcome to the BLK LIST! You will receive notifications of upcoming drops.');
      setEmail('');
    } catch (error) {
      if (error.response?.status === 409) {
        toast.info('Already subscribed!');
      } else {
        toast.error('Failed to subscribe');
      }
    } finally {
      setSubscribing(false);
    }
  };

  const handleEnterDrop = () => {
    // Require login to view catalog
    // Check token existence as source of truth (handles race condition)
    if (user || hasToken) {
      navigate('/catalog');
    } else {
      navigate('/login');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono text-xl cursor-blink">INITIALIZING</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden flex flex-col">
      {/* Header */}
      <header className="fixed left-0 right-0 z-50 border-b border-zinc-800 bg-[#050505]/90 backdrop-blur-sm" style={{ top: 'var(--banner-offset, 0px)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="BLK MRKT" className="h-8 w-auto" />
          </Link>
          
          <nav className="flex items-center gap-4">
            {(user || hasToken) ? (
              <>
                <Link 
                  to="/catalog"
                  className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                  data-testid="home-link"
                >
                  <Home className="w-5 h-5" />
                </Link>
                <Link 
                  to="/cart" 
                  className="relative p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                  data-testid="cart-link"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {itemCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-tiffany-500 text-black text-xs font-bold w-5 h-5 flex items-center justify-center">
                      {itemCount}
                    </span>
                  )}
                </Link>
                <Link 
                  to="/dashboard" 
                  className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                  data-testid="dashboard-link"
                >
                  <User className="w-5 h-5" />
                </Link>
                {user?.is_admin && (
                  <Link 
                    to="/admin" 
                    className="p-2 text-zinc-400 hover:text-tiffany-500 transition-colors"
                    data-testid="admin-link"
                  >
                    <Shield className="w-5 h-5" />
                  </Link>
                )}
                <button 
                  onClick={logout}
                  className="p-2 text-zinc-400 hover:text-red-500 transition-colors"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-5 h-5" />
                </button>
              </>
            ) : (
              <div className="flex items-center gap-4">
                <Link 
                  to="/login"
                  className="text-sm font-mono text-tiffany-500 hover:underline"
                  data-testid="login-link"
                >
                  LOGIN
                </Link>
                <Link 
                  to="/register"
                  className="text-sm font-mono text-tiffany-500 hover:underline"
                  data-testid="register-link"
                >
                  REGISTER
                </Link>
              </div>
            )}
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <main className="min-h-screen flex flex-col items-center justify-center px-4 pt-16">
        {/* Logo */}
        <div className="mb-12 fade-in">
          <img 
            src={LOGO_URL} 
            alt="BLK MRKT" 
            className="w-64 md:w-96 h-auto"
          />
        </div>

        {/* Drop Status */}
        <div className="text-center mb-12 fade-in stagger-1">
          <div className="flex items-center justify-center gap-3 mb-6">
            {dropStatus.is_open || hasAccess ? (
              <>
                <Unlock className="w-5 h-5 text-tiffany-500" />
                <span className="font-accent text-sm text-tiffany-500 tracking-widest">DROP ACTIVE</span>
              </>
            ) : dropStatus.fbcon_mode ? (
              <>
                <Lock className="w-5 h-5 text-purple-400" />
                <span className="font-accent text-sm text-purple-400 tracking-widest">DROP CLOSED</span>
              </>
            ) : (
              <>
                <Lock className="w-5 h-5 text-zinc-500" />
                <span className="font-accent text-sm text-zinc-500 tracking-widest">DROP CLOSED</span>
              </>
            )}
          </div>
          
          {/* FBCON special message */}
          {!hasAccess && !dropStatus.is_open && dropStatus.fbcon_mode && (
            <div className="mb-6">
              <p className="font-mono text-purple-400 text-lg tracking-widest mb-2">FINGERBOARD CON</p>
              <p className="font-mono text-zinc-400 text-sm">4/24 - 4/26</p>
            </div>
          )}
          
          {/* Show countdown timer when drop is scheduled */}
          {!hasAccess && !dropStatus.is_open && dropStatus.scheduled_drop_time && (
            <div className="mt-8 mb-4">
              <DropCountdown 
                scheduledTime={dropStatus.scheduled_drop_time} 
                onComplete={handleCountdownComplete}
              />
              
              {/* Remind Me Button */}
              <div className="mt-6">
                {reminderSubscribed ? (
                  <div className="flex items-center justify-center gap-2 text-tiffany-500">
                    <BellRing className="w-4 h-4" />
                    <span className="font-mono text-xs tracking-widest">REMINDER SET</span>
                  </div>
                ) : showReminderInput ? (
                  <div className="flex gap-2 max-w-xs mx-auto">
                    <Input
                      type="email"
                      placeholder="your@email.com"
                      value={reminderEmail}
                      onChange={(e) => setReminderEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleReminderSubscribe()}
                      className="flex-1 h-10 bg-zinc-900/50 border-zinc-700 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                      data-testid="reminder-email-input"
                      autoFocus
                    />
                    <Button
                      onClick={handleReminderSubscribe}
                      disabled={subscribingReminder}
                      className="h-10 px-4 bg-amber-500/20 text-amber-400 hover:bg-amber-500/30 font-mono rounded-none border border-amber-500/50"
                      data-testid="reminder-subscribe-btn"
                    >
                      <Bell className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    onClick={() => setShowReminderInput(true)}
                    className="h-10 px-6 bg-transparent border border-amber-500/50 text-amber-400 hover:bg-amber-500/20 font-mono text-xs tracking-widest rounded-none"
                    data-testid="show-reminder-btn"
                  >
                    <Bell className="w-4 h-4 mr-2" />
                    REMIND ME 10 MINUTES BEFORE THE DROP
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Access Control */}
        <div className="w-full max-w-md fade-in stagger-2">
          {hasAccess ? (
            <Button
              onClick={handleEnterDrop}
              className="w-full h-14 bg-transparent border-2 border-tiffany-500 text-tiffany-500 hover:bg-tiffany-500 hover:text-black font-sans text-lg tracking-widest transition-all duration-300 rounded-none"
              data-testid="enter-drop-btn"
            >
              ENTER THE DROP
            </Button>
          ) : (
            <div className="space-y-6">
              <p className="text-center text-zinc-500 font-mono text-sm">
                ENTER ACCESS CODE TO CONTINUE
              </p>
              <div className="flex gap-2">
                <Input
                  type="text"
                  placeholder="ACCESS CODE"
                  value={accessCode}
                  onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === 'Enter' && checkAccessCode()}
                  className="flex-1 h-14 bg-zinc-900 border-zinc-800 text-white font-mono text-center text-lg tracking-widest placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                  data-testid="access-code-input"
                />
                <Button
                  onClick={checkAccessCode}
                  className="h-14 px-8 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none"
                  data-testid="submit-code-btn"
                >
                  SUBMIT
                </Button>
              </div>

              {/* Mailing List Signup */}
              <div className="pt-8 mt-8 border-t border-zinc-800/50 mb-16">
                {!showEmailInput ? (
                  <Button
                    onClick={() => setShowEmailInput(true)}
                    className="w-full h-14 bg-tiffany-500 text-black hover:bg-tiffany-400 font-sans tracking-widest rounded-none text-xs sm:text-sm"
                    data-testid="show-email-btn"
                  >
                    CLICK TO JOIN THE BLK LIST FOR FUTURE DROPS AND UPDATES.
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <p className="text-center text-zinc-500 font-mono text-xs">
                      JOIN THE BLK LIST
                    </p>
                    <div className="flex gap-2 max-w-xs mx-auto">
                      <Input
                        type="email"
                        placeholder="your@email.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSubscribe()}
                        className="flex-1 h-10 bg-zinc-900/50 border-zinc-800 text-white font-mono text-sm placeholder:text-zinc-600 focus:border-tiffany-500 rounded-none"
                        data-testid="mailing-list-email"
                        autoFocus
                      />
                      <Button
                        onClick={handleSubscribe}
                        disabled={subscribing}
                        className="h-10 px-4 bg-zinc-800/50 text-tiffany-500 hover:bg-zinc-700 font-mono rounded-none border border-zinc-700/50"
                        data-testid="subscribe-btn"
                      >
                        <Mail className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
